import {
  getIsSpanishIslandsResidentFareAvailable,
  getIsFlexibleTicketAvailable,
  getIsFlexibleTicketPreSelectedAtMeta,
  getIsSeatMapAvailable,
  getIsCfarAvailable
} from "../../store/selectors/checkoutSteps";
import { useSelector } from "react-redux";
import { useMemo } from "react";
import useIsBrandedFare from "../../branded-fares/hooks/useIsBrandedFare";
import { getAncillaries, getCanSkipExtrasPage } from "../../store/flightDetails/selectors";
import flights_web_pax_before_flex_ticket from "../../utils/experiments/funnel/flights_web_pax_before_flex_ticket";
import { useFlightsWebMdotPaxBeforeCp } from "../../utils/experiments/funnel/flights_web_mdot_pax_before_cp";
import useUserAgent from "../useUserAgent";
import { RouteName } from "@flights/constants";

type Step = {
  step: Extract<
    RouteName,
    | "checkout-start"
    | "checkout-fare"
    | "checkout-ticket-type"
    | "checkout-pax"
    | "checkout-extras"
    | "checkout-seat-selection"
    | "checkout-sirf"
    | "checkout-payment"
  >;
  when: () => boolean;
  requiresAncillaries?: boolean;
  requiresOrder?: boolean;
};

const useAvailableCheckoutSteps = () => {
  const { isMobile } = useUserAgent();
  const isBrandedFare = useIsBrandedFare();
  const isSpanishIslandsResidentFareAvailable = useSelector(getIsSpanishIslandsResidentFareAvailable);
  const isFlexibleTicketPreSelectedAtMeta = useSelector(getIsFlexibleTicketPreSelectedAtMeta);
  const isCfarAvailable = useSelector(getIsCfarAvailable);
  const isFlexibleTicketAvailable = useSelector(getIsFlexibleTicketAvailable);
  const isSeatMapAvailable = useSelector(getIsSeatMapAvailable);
  const skipExtrasPage = useSelector(getCanSkipExtrasPage);
  const areTicketTypesAvailable = isFlexibleTicketAvailable || isCfarAvailable;
  const showTicketTypePage = areTicketTypesAvailable && !isBrandedFare && !isFlexibleTicketPreSelectedAtMeta;
  const showBrandedFarePage = isBrandedFare && !isFlexibleTicketPreSelectedAtMeta;
  const ancillaries = useSelector(getAncillaries);
  const showExtrasPage = ancillaries && !!skipExtrasPage ? Object.keys(ancillaries).length > 0 : true;
  const { isVariant } = useFlightsWebMdotPaxBeforeCp();

  flights_web_pax_before_flex_ticket.useTrackStages(showTicketTypePage && !isMobile);

  const steps: Array<Step> = useMemo(() => {
    const pax = { step: "checkout-pax", when: () => true } as Step;
    return [
      { step: "checkout-start", when: () => true },
      { step: "checkout-fare", when: () => showBrandedFarePage },

      ...(showTicketTypePage && !isMobile && flights_web_pax_before_flex_ticket.trackWithDefaultStage()
        ? [...(isMobile && isVariant() ? [] : [pax]), { step: "checkout-ticket-type", when: () => true } as Step]
        : [
            { step: "checkout-ticket-type", when: () => showTicketTypePage } as Step,
            ...(isMobile && isVariant() ? [] : [pax])
          ]),

      { step: "checkout-extras", when: () => showExtrasPage },
      { step: "checkout-seat-selection", when: () => isSeatMapAvailable },
      ...(isMobile && isVariant() ? [pax] : []),
      {
        step: "checkout-sirf",
        when: () => isSpanishIslandsResidentFareAvailable,
        requiresAncillaries: true
      },
      {
        step: "checkout-payment",
        when: () => true,
        requiresAncillaries: true,
        requiresOrder: true
      }
    ];
  }, [
    isVariant,
    isMobile,
    isSeatMapAvailable,
    isSpanishIslandsResidentFareAvailable,
    showBrandedFarePage,
    showExtrasPage,
    showTicketTypePage
  ]);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const activeSteps = useMemo(() => steps.filter(({ when }) => when()).map(({ when, ...rest }) => rest), [steps]);

  return activeSteps;
};

export default useAvailableCheckoutSteps;
